import { gql } from '@apollo/client';

/*
** GET MEDIA CATEGORIES **
*/

export const GET_MEDIA_CATEGORIES = gql`
  query getMediaCategories {
    getMediaCategories {
      label
      value
    }
  }
`;

export type MediaCategory = {
  label: string;
  value: string;
};

export type GetMediaCategoriesData = {
  getMediaCategories: MediaCategory[];
};

/*
** MULTIPART UPLOAD **
*/

export const START_MULTIPART_UPLOAD = gql`
  query startS3MultipartUpload($contentType: String!, $key: String!) {
    startS3MultipartUpload(key: $key, contentType: $contentType) {
      key
      uploadId
    }
  }
`;

export type StartMultipartUploadVariables = {
  contentType: string;
  key: string;
};

export type StartMultipartUploadData = {
  startS3MultipartUpload: {
    key: string;
    uploadId: string;
  };
};

export const GET_MULTIPART_PRESIGNED_URL = gql`
  query getS3MultipartPreSignedUrl($key: String!, $partNumber: Int!, $uploadId: String!) {
    getS3MultipartPreSignedUrl(key: $key, partNumber: $partNumber, uploadId: $uploadId) {
      partNumber
      preSignedUrl
    }
  }
`;

export type GetMultipartPreSignedUrlVariables = {
  key: string;
  partNumber: number;
  uploadId: string;
};

export type GetMultipartPreSignedUrlData = {
  getS3MultipartPreSignedUrl: {
    partNumber: number;
    preSignedUrl: string;
  };
};

export const COMPLETE_MULTIPART_UPLOAD = gql`
  query completeS3MultipartUpload($key: String!, $uploadId: String!, $parts: [PartInput!]!, $maId: String!) {
    completeS3MultipartUpload(key: $key, parts: $parts, uploadId: $uploadId, maId: $maId) {
      location
      bucket
      key
      eTag
    }
  }
`;

export type PartInput = {
  eTag: string;
  partNumber: number;
};

export type CompleteMultipartUploadVariables = {
  key: string;
  uploadId: string;
  parts: PartInput[];
  maId: string;
};

export type CompleteMultipartUploadData = {
  completeS3MultipartUpload: {
    location: string;
    bucket: string;
    key: string;
    eTag: string;
  };
};

export const LIST_S3_PARTS = gql`
  query listS3Parts($key: String!, $uploadId: String!) {
    listS3Parts(key: $key, uploadId: $uploadId) {
      eTag
      partNumber
      size
    }
  }
`;

/*
** THUMBNAIL UPLOAD **
*/

export const GET_S3_UPLOAD_PRESIGNED_URL = gql`
  query getS3UploadPresignedUrl($key: String!) {
    getS3UploadPresignedUrl(key: $key) {
      preSignedUrl
      region
    }
  }
`;

export type GetS3UploadPresignedUrlData = {
  getS3UploadPresignedUrl: {
    preSignedUrl: string;
    region: string;
  };
};

/*
** MY VIDEOS **
*/

export const GET_MY_VIDEOS = gql`
  query allMediaApprovals($filter: MediaApprovalFilter, $orderBy: [MediaApprovalsOrderBy!] = MA_UPDATED_AT_DESC, $offset: Int!, $first: Int!) {
    allMediaApprovals(filter: $filter, first: $first, offset: $offset, orderBy: $orderBy) {
      nodes {
        maId
        maTags
        maTitle
        maType
        maCategory
        maDescription
        maStatus
        maDuration
        maS3MediaKeyUrl
        maS3ThumbnailKeyUrl
        dropdownByAgeRatingId {
          ddLabel
          ddId
          ddValue
        }
        maAdditionalImages
        genreDropdownsByMaId {
          nodes {
            dropdownByDdId {
              ddId
              ddLabel
              ddValue
            }
          }
        }
        userByMaUploadedBy {
          uId
          uFullName
          uEmailId
          uAvatarUrl
        }
      }
      totalCount
    }
  }
`;

export type User = {
  uId: string;
  uFullName: string;
  uEmailId: string;
  uAvatarUrl: string;
};

export type AdditionalImage = {
  maAdditionalImagesS3KeyUrl: string;
  maAdditionalImagesLabelId: string;
  maAdditionalImagesLabelName: string;
  maAdditionalImagesLabelValue: string;
};

export type MyVideo = {
  maId: string;
  maTags: string[];
  maTitle: string;
  maType: string;
  maCategory: string;
  maDescription: string;
  maStatus: 'APPROVED' | 'PENDING' | 'REJECTED';
  maDuration?: string;
  maS3MediaKeyUrl: string;
  maS3ThumbnailKeyUrl: string;
  dropdownByAgeRatingId?: {
    ddLabel: string;
    ddId: string;
    ddValue: string;
  };
  maAdditionalImages?: AdditionalImage[];
  genreDropdownsByMaId?: {
    nodes: {
      dropdownByDdId: {
        ddId: string;
        ddLabel: string;
        ddValue: string;
      };
    }[];
  };
  userByMaUploadedBy: User;
};

export type GetMyVideosData = {
  allMediaApprovals: {
    nodes: MyVideo[];
    totalCount: number;
  };
};

export type StringFilter = {
  equalTo?: string;
  isNull?: boolean;
};

export type MediaApprovalFilter = {
  maId?: StringFilter;
  maType?: StringFilter;
  maUploadStatus?: StringFilter;
  maDeletedAt?: { isNull?: boolean };
  maUploadedBy?: StringFilter;
};

export type MediaApprovalsOrderBy = 'MA_UPDATED_AT_DESC' | 'MA_UPDATED_AT_ASC';

export type GetMyVideosVariables = {
  filter: MediaApprovalFilter;
  orderBy?: MediaApprovalsOrderBy[];
  offset: number;
  first: number;
};
